import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { Anchor, Search, Trash2, Plus, Loader2, UserPlus } from 'lucide-react';
import { api } from '@/lib/api';
import { useBoatContextStore } from '@/stores/boat-context-store';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { UserBoat, MyBoatsResponse, SearchSuggestionsResponse } from '@regatta-replay/types';

interface MyBoatsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BoatSuggestion {
  id: string;
  name: string;
  subtitle?: string;
  meta?: { class?: string; sailNumber?: string };
}

export function MyBoatsModal({ open, onOpenChange }: MyBoatsModalProps) {
  const { setBoats } = useBoatContextStore();
  const [ownedBoats, setOwnedBoats] = useState<UserBoat[]>([]);
  const [crewBoats, setCrewBoats] = useState<UserBoat[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<BoatSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [addingCrew, setAddingCrew] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [relationship, setRelationship] = useState<'owner' | 'crew'>('owner');
  const [crewBoatId, setCrewBoatId] = useState<string | null>(null);
  const [crewDisplayName, setCrewDisplayName] = useState('');
  const [crewEmail, setCrewEmail] = useState('');
  const [crewRole, setCrewRole] = useState('crew');
  const [error, setError] = useState<string | null>(null);

  const loadMyBoats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<MyBoatsResponse>('/unified-search/me/boats', { silent: true });
      setOwnedBoats(data.owned);
      setCrewBoats(data.crewing);
      setBoats(data.owned, data.crewing);
    } catch {
      // silently fail — toast handled by api helper
    } finally {
      setLoading(false);
    }
  }, [setBoats]);

  useEffect(() => {
    if (open) {
      loadMyBoats();
      setSearchQuery('');
      setSuggestions([]);
      setCrewBoatId(null);
      setCrewDisplayName('');
      setCrewEmail('');
      setCrewRole('crew');
      setError(null);
    }
  }, [open, loadMyBoats]);

  // Search for boats as user types
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const data = await api.get<SearchSuggestionsResponse>(
          `/unified-search/suggest?q=${encodeURIComponent(searchQuery)}&limit=5`,
          { silent: true },
        );
        setSuggestions(data.boats ?? []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const allBoatIds = new Set([
    ...ownedBoats.map((b) => b.boatId),
    ...crewBoats.map((b) => b.boatId),
  ]);

  async function handleAdd(boatId: string) {
    setAdding(boatId);
    setError(null);
    try {
      await api.post('/me/boats', { boatId, relationship });
      await loadMyBoats();
      setSearchQuery('');
      setSuggestions([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add boat');
    } finally {
      setAdding(null);
    }
  }

  async function handleAddCrew(event: FormEvent) {
    event.preventDefault();
    if (!crewBoatId) return;

    setAddingCrew(crewBoatId);
    setError(null);
    try {
      await api.post(`/me/boats/${crewBoatId}/crew`, {
        displayName: crewDisplayName.trim(),
        email: crewEmail.trim() || undefined,
        role: crewRole.trim() || 'crew',
      });
      await loadMyBoats();
      setCrewBoatId(null);
      setCrewDisplayName('');
      setCrewEmail('');
      setCrewRole('crew');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add crew');
    } finally {
      setAddingCrew(null);
    }
  }

  async function handleRemove(boatId: string) {
    setRemoving(boatId);
    setError(null);
    try {
      await api.delete('/me/boats/' + boatId);
      await loadMyBoats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove boat');
    } finally {
      setRemoving(null);
    }
  }

  const hasBoats = ownedBoats.length > 0 || crewBoats.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Anchor size={18} />
            My Boats
          </DialogTitle>
          <DialogDescription>
            Add boats you own or crew on to personalize your experience.
          </DialogDescription>
        </DialogHeader>

        {/* Search section */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by boat name or sail number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <select
              value={relationship}
              onChange={(e) => setRelationship(e.target.value as 'owner' | 'crew')}
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="owner">Owner</option>
              <option value="crew">Crew</option>
            </select>
          </div>

          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
              {suggestions.map((boat) => {
                const alreadyAdded = allBoatIds.has(boat.id);
                return (
                  <div
                    key={boat.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-slate-50"
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-[13px] font-medium text-navy-900 truncate">
                        {boat.meta?.class && (
                          <span className="text-slate-400 font-normal">{boat.meta.class} · </span>
                        )}
                        {boat.name}
                      </span>
                      {boat.subtitle && (
                        <span className="text-[11px] text-slate-400">{boat.subtitle}</span>
                      )}
                    </div>
                    {alreadyAdded ? (
                      <span className="text-[11px] text-slate-400 shrink-0 ml-2">Already added</span>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0 ml-2 h-7 px-2 text-brand-ink hover:text-brand-ink hover:bg-brand-blue/10"
                        disabled={adding === boat.id}
                        onClick={() => handleAdd(boat.id)}
                      >
                        {adding === boat.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Plus size={14} />
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {searchQuery.length >= 2 && suggestions.length === 0 && !searching && (
            <p className="text-[13px] text-slate-400 text-center py-2">No boats found</p>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Current boats */}
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : hasBoats ? (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {ownedBoats.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  My Boats
                </div>
                {ownedBoats.map((boat) => {
                  const crewFormOpen = crewBoatId === boat.boatId;
                  return (
                    <div key={boat.boatId}>
                      <BoatRow
                        boat={boat}
                        removing={removing === boat.boatId}
                        onRemove={() => handleRemove(boat.boatId)}
                        onAddCrew={() => {
                          setCrewBoatId(crewFormOpen ? null : boat.boatId);
                          setCrewDisplayName('');
                          setCrewEmail('');
                          setCrewRole('crew');
                          setError(null);
                        }}
                      />
                      {crewFormOpen && (
                        <form
                          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_90px_auto] gap-2 pb-2 pl-6"
                          onSubmit={handleAddCrew}
                        >
                          <Input
                            aria-label="Crew name"
                            placeholder="Crew name"
                            value={crewDisplayName}
                            onChange={(event) => setCrewDisplayName(event.target.value)}
                            required
                          />
                          <Input
                            aria-label="Crew email"
                            placeholder="Email (optional)"
                            type="email"
                            value={crewEmail}
                            onChange={(event) => setCrewEmail(event.target.value)}
                          />
                          <Input
                            aria-label="Crew role"
                            placeholder="Role"
                            value={crewRole}
                            onChange={(event) => setCrewRole(event.target.value)}
                          />
                          <Button
                            type="submit"
                            size="sm"
                            className="h-10"
                            disabled={addingCrew === boat.boatId}
                          >
                            {addingCrew === boat.boatId ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              'Add crew'
                            )}
                          </Button>
                        </form>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {crewBoats.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 pt-2 border-t border-slate-100">
                  Boats I Crew On
                </div>
                {crewBoats.map((boat) => (
                  <BoatRow
                    key={boat.boatId}
                    boat={boat}
                    removing={removing === boat.boatId}
                    onRemove={() => handleRemove(boat.boatId)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-400 text-sm">
            No boats configured yet. Search above to add your first boat.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BoatRow({
  boat,
  removing,
  onRemove,
  onAddCrew,
}: {
  boat: UserBoat;
  removing: boolean;
  onRemove: () => void;
  onAddCrew?: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 group">
      <div className="flex items-center gap-2 min-w-0">
        <Anchor size={14} className="text-slate-400 shrink-0" />
        <span className="text-[13px] font-medium text-navy-900 truncate">
          {boat.classDisplayName && (
            <span className="text-slate-400 font-normal">{boat.classDisplayName} · </span>
          )}
          {boat.sailCountry} {boat.sailNumber} {boat.boatName}
        </span>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        {onAddCrew && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-slate-400 hover:text-brand-ink hover:bg-brand-blue/10"
            onClick={onAddCrew}
            aria-label={`Add crew to ${boat.boatName || boat.sailNumber}`}
            title="Add crew"
          >
            <UserPlus size={14} />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50"
          disabled={removing}
          onClick={onRemove}
          aria-label={`Remove ${boat.boatName || boat.sailNumber}`}
        >
          {removing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
        </Button>
      </div>
    </div>
  );
}
